import { useEffect, useState } from 'react';
import { observer } from 'mobx-react-lite';
import {
  ViewCustomizer,
  type CustomizerItem,
  type CustomizerScope,
  type CustomizerScopeKind,
} from './ViewCustomizer';

/** Settings of a view that shows a chosen, ordered set of items. */
export type ItemViewSettings = { visible: string[]; known: string[] };

/**
 * The parts of a view settings store this dialog needs (see
 * createViewSettingsStore in @emfit/shared-store).
 */
export interface CustomizableViewStore<T extends ItemViewSettings> {
  settings: T;
  isEditing: boolean;
  startEditing(): void;
  update(patch: Partial<T>): void;
  cancelEditing(): void;
  resetDraft(kind: CustomizerScopeKind): Promise<void>;
  save(kind: CustomizerScopeKind, alsoClearDevices?: string[]): Promise<void>;
}

export type CustomizeViewDialogProps<T extends ItemViewSettings> = {
  store: CustomizableViewStore<T>;
  isOpen: boolean;
  onClose: () => void;
  title: string;
  /** Every item the view offers, in default order */
  items: CustomizerItem[];
  /** The visible items as the view resolves them now */
  visible: string[];
  scopes: CustomizerScope[];
  initialScope: CustomizerScopeKind;
  /** Device overrides to drop when saving for all of the user's devices */
  deviceIds?: string[];
  /** View-specific settings rendered under the item list */
  children?: React.ReactNode;
  t?: (key: string) => string;
};

/**
 * ViewCustomizer bound to a view settings store: opening starts a draft,
 * Cancel drops it, Save stores it for the chosen scope and closes.
 */
export const CustomizeViewDialog = observer(function CustomizeViewDialog<T extends ItemViewSettings>({
  store,
  isOpen,
  onClose,
  title,
  items,
  visible,
  scopes,
  initialScope,
  deviceIds = [],
  children,
  t,
}: CustomizeViewDialogProps<T>) {
  const [scope, setScope] = useState<CustomizerScopeKind>(initialScope);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!isOpen) return;
    setScope(initialScope);
    store.startEditing();
    store.update({ visible, known: items.map((item) => item.id) } as Partial<T>);
    // Only when the dialog opens: the draft then belongs to the user.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  const close = () => {
    store.cancelEditing();
    onClose();
  };

  return (
    <ViewCustomizer
      isOpen={isOpen}
      onClose={close}
      title={title}
      items={items}
      visible={store.isEditing ? store.settings.visible : visible}
      onVisibleChange={(next) =>
        store.update({ visible: next, known: items.map((item) => item.id) } as Partial<T>)
      }
      scopes={scopes}
      scope={scope}
      onScopeChange={setScope}
      saving={saving}
      onReset={() => store.resetDraft(scope)}
      onSave={async () => {
        setSaving(true);
        try {
          await store.save(scope, scope === 'user' ? deviceIds : []);
          onClose();
        } finally {
          setSaving(false);
        }
      }}
      t={t}
    >
      {children}
    </ViewCustomizer>
  );
}) as <T extends ItemViewSettings>(props: CustomizeViewDialogProps<T>) => JSX.Element;
